import React, { lazy, useEffect } from "react";
import { I18n } from "react-redux-i18n";
import { BrowserRouter, Route, Routes } from "react-router-dom";

// Layout persistente
import PersistentLayout from "app/layouts/PersistentLayout.jsx";
// Lazy loading para componentes de autenticação (críticos - carregados imediatamente)
import AuthAssertion from "containers/authAssertion/AuthAssertion.jsx";
import Error from "containers/error/Error.jsx";
import Login from "containers/login/Login.jsx";

// Lazy loading para componentes do painel (carregados sob demanda)
const AccessConsolidated = lazy(
  () => import("containers/panel/access-consolidated/AccessConsolidated.jsx")
);
const AccessHashAgency = lazy(
  () => import("containers/panel/access-hash-agency/AccessHashAgency.jsx")
);
const AccessRedeFacilCode = lazy(
  () =>
    import("containers/panel/access-rede-facil-code/AccessRedeFacilCode.jsx")
);
const CitiesBlockList = lazy(
  () => import("containers/panel/block-list/CitiesBlockList.jsx")
);
const ClientsBlockList = lazy(
  () => import("containers/panel/block-list/ClientsBlockList.jsx")
);
const CityBlockListForm = lazy(
  () => import("containers/panel/city-block-list-form/CityBlockListForm.jsx")
);
const ClientBlockListForm = lazy(
  () =>
    import("containers/panel/client-block-list-form/ClientBlockListForm.jsx")
);
const ClientLogins = lazy(
  () => import("containers/panel/client-logins/ClientLogins.jsx")
);
const ClientDetail = lazy(
  () => import("containers/panel/client/client-detail/ClientDetail.jsx")
);
const Client = lazy(() => import("containers/panel/client/Client.jsx"));

const Complaint = lazy(
  () => import("containers/panel/client/complaint/Complaint.jsx")
);
const ClientsConsolidated = lazy(
  () => import("containers/panel/clients-consolidated/ClientsConsolidated.jsx")
);
const ContractConsolidated = lazy(
  () =>
    import("containers/panel/contract-consolidated/ContractConsolidated.jsx")
);
const ContractDetail = lazy(
  () => import("containers/panel/contract/contract-detail/ContractDetail.jsx")
);
const Contract = lazy(() => import("containers/panel/contract/Contract.jsx"));
const DashboardConsolidated = lazy(
  () =>
    import("containers/panel/dashboard-consolidated/DashboardConsolidated.jsx")
);
const DistributorDetail = lazy(
  () =>
    import(
      "containers/panel/distributor/distributor-detail/DistributorDetail.jsx"
    )
);
const Distributor = lazy(
  () => import("containers/panel/distributor/Distributor.jsx")
);
const Faq = lazy(() => import("containers/panel/faq/Faq.jsx"));
const HashAgencyClient = lazy(
  () => import("containers/panel/hash-agency/Hash-agency.jsx")
);
const Installment = lazy(
  () => import("containers/panel/installment/Installment.jsx")
);
const JobLog = lazy(() => import("containers/panel/joblog/JobLog.jsx"));
const JobMonitoring = lazy(
  () => import("containers/panel/jobMonitoring/JobMonitoring.jsx")
);
const LogErrors = lazy(
  () =>
    import("containers/panel/log-errors-consolidated/LogErrorsConsolidated.jsx")
);
const Log = lazy(() => import("containers/panel/log/Log.jsx"));
const PartnerDetail = lazy(
  () => import("containers/panel/partner/partner-detail/PartnerDetail.jsx")
);
const Partner = lazy(() => import("containers/panel/partner/Partner.jsx"));
const PreContractConsolidated = lazy(
  () =>
    import(
      "containers/panel/pre-contract-consolidated/PreContractConsolidated.jsx"
    )
);
const PreContract = lazy(
  () => import("containers/panel/pre-contract/PreContract.jsx")
);
const ReportDownloads = lazy(
  () => import("containers/panel/report-downloads/ReportDownloads.jsx")
);
const SimulationPerClient = lazy(
  () =>
    import(
      "containers/panel/simulation-per-client-consolidated/SimulationPerClientConsolidated.jsx"
    )
);
const UserCPFLDetail = lazy(
  () => import("containers/panel/user-cpfl/user-cpfl-detail/UserCPFLDetail.jsx")
);
const UserCPFL = lazy(() => import("containers/panel/user-cpfl/UserCPFL.jsx"));
const UserPartnerDetail = lazy(
  () =>
    import(
      "containers/panel/user-partner/user-partner-detail/UserPartnerDetail.jsx"
    )
);
const UserPartner = lazy(
  () => import("containers/panel/user-partner/UserPartner.jsx")
);

import Public from "./Public.jsx";

const PublicRoute = ({ title, container, ...props }) => (
  <Public title={title} container={container} {...props} />
);

const PageTitle = ({ title, children }) => {
  useEffect(() => {
    document.title = title || "CPFL Finergy";
  }, [title]);

  return <>{children}</>;
};

class Content extends React.PureComponent {
  render() {
    return (
      <BrowserRouter>
        <Routes>
          {/* Rotas públicas (sem layout persistente) */}
          <Route
            path={I18n.t("routes.authAssertion.url")}
            element={
              <PublicRoute
                container={<AuthAssertion />}
                title={`${I18n.t("routes.authAssertion.pageTitle")} - ${I18n.t("application.name")}`}
              />
            }
          />
          <Route
            path={I18n.t("routes.login.url")}
            element={
              <PublicRoute
                container={<Login />}
                title={`${I18n.t("routes.login.pageTitle")} - ${I18n.t("application.name")}`}
              />
            }
          />

          {/* Rotas autenticadas com layout persistente */}
          <Route path="/*" element={<PersistentLayout />}>
            <Route
              path="partner"
              element={
                <PageTitle
                  title={`${I18n.t("routes.partner.pageTitle")} - ${I18n.t("application.name")}`}
                >
                  <Partner />
                </PageTitle>
              }
            />
            <Route
              path="partner/:id"
              element={
                <PageTitle
                  title={`${I18n.t("routes.partnerDetail.pageTitle")} - ${I18n.t("application.name")}`}
                >
                  <PartnerDetail />
                </PageTitle>
              }
            />
            <Route
              path="distributor"
              element={
                <PageTitle
                  title={`${I18n.t("routes.distributor.pageTitle")} - ${I18n.t("application.name")}`}
                >
                  <Distributor />
                </PageTitle>
              }
            />
            <Route
              path="distributor/:id"
              element={
                <PageTitle
                  title={`${I18n.t("routes.distributorDetail.pageTitle")} - ${I18n.t("application.name")}`}
                >
                  <DistributorDetail />
                </PageTitle>
              }
            />
            <Route
              path="userCPFL"
              element={
                <PageTitle
                  title={`${I18n.t("routes.userCPFL.pageTitle")} - ${I18n.t("application.name")}`}
                >
                  <UserCPFL />
                </PageTitle>
              }
            />
            <Route
              path="userCPFL/:id"
              element={
                <PageTitle
                  title={`${I18n.t("routes.userCPFLDetail.pageTitle")} - ${I18n.t("application.name")}`}
                >
                  <UserCPFLDetail />
                </PageTitle>
              }
            />
            <Route
              path="userPartner"
              element={
                <PageTitle
                  title={`${I18n.t("routes.userPartner.pageTitle")} - ${I18n.t("application.name")}`}
                >
                  <UserPartner />
                </PageTitle>
              }
            />
            <Route
              path="userPartner/:id"
              element={
                <PageTitle
                  title={`${I18n.t("routes.userPartnerDetail.pageTitle")} - ${I18n.t("application.name")}`}
                >
                  <UserPartnerDetail />
                </PageTitle>
              }
            />
            <Route
              path="installment"
              element={
                <PageTitle
                  title={`${I18n.t("routes.installment.pageTitle")} - ${I18n.t("application.name")}`}
                >
                  <Installment />
                </PageTitle>
              }
            />
            <Route
              path="contract"
              element={
                <PageTitle
                  title={`${I18n.t("routes.contract.pageTitle")} - ${I18n.t("application.name")}`}
                >
                  <Contract />
                </PageTitle>
              }
            />
            <Route
              path="consolidated/contract"
              element={
                <PageTitle
                  title={`${I18n.t("routes.contractConsolidated.pageTitle")} - ${I18n.t("application.name")}`}
                >
                  <ContractConsolidated />
                </PageTitle>
              }
            />
            <Route
              path="consolidated/simulation-approves-per-client"
              element={
                <PageTitle
                  title={`${I18n.t("routes.simulationPerClient.pageTitle")} - ${I18n.t("application.name")}`}
                >
                  <SimulationPerClient />
                </PageTitle>
              }
            />
            <Route
              path="contract/:id"
              element={
                <PageTitle
                  title={`${I18n.t("routes.contractDetail.pageTitle")} - ${I18n.t("application.name")}`}
                >
                  <ContractDetail />
                </PageTitle>
              }
            />
            <Route
              path="consolidated/dashboard"
              element={
                <PageTitle
                  title={`${I18n.t("routes.dashboardConsolidated.pageTitle")} - ${I18n.t("application.name")}`}
                >
                  <DashboardConsolidated />
                </PageTitle>
              }
            />
            <Route
              path="consolidated/pre-contract"
              element={
                <PageTitle
                  title={`${I18n.t("routes.preContractConsolidated.pageTitle")} - ${I18n.t("application.name")}`}
                >
                  <PreContractConsolidated />
                </PageTitle>
              }
            />
            <Route
              path="pre-contract"
              element={
                <PageTitle
                  title={`${I18n.t("routes.preContract.pageTitle")} - ${I18n.t("application.name")}`}
                >
                  <PreContract />
                </PageTitle>
              }
            />
            <Route
              path="consolidated/errors"
              element={
                <PageTitle
                  title={`${I18n.t("routes.logErrorsConsolidated.pageTitle")} - ${I18n.t("application.name")}`}
                >
                  <LogErrors />
                </PageTitle>
              }
            />
            <Route
              path="joblogs"
              element={
                <PageTitle
                  title={`${I18n.t("routes.jobLog.pageTitle")} - ${I18n.t("application.name")}`}
                >
                  <JobLog />
                </PageTitle>
              }
            />
            <Route
              path="jobMonitoring"
              element={
                <PageTitle
                  title={`${I18n.t("routes.jobMonitoring.pageTitle")} - ${I18n.t("application.name")}`}
                >
                  <JobMonitoring />
                </PageTitle>
              }
            />
            <Route
              path="jobmonitoring"
              element={
                <PageTitle
                  title={`${I18n.t("routes.jobMonitoring.pageTitle")} - ${I18n.t("application.name")}`}
                >
                  <JobMonitoring />
                </PageTitle>
              }
            />
            <Route
              path="JobMonitoring"
              element={
                <PageTitle
                  title={`${I18n.t("routes.jobMonitoring.pageTitle")} - ${I18n.t("application.name")}`}
                >
                  <JobMonitoring />
                </PageTitle>
              }
            />
            <Route
              path="client"
              element={
                <PageTitle
                  title={`${I18n.t("routes.client.pageTitle")} - ${I18n.t("application.name")}`}
                >
                  <Client />
                </PageTitle>
              }
            />
            <Route
              path="block-list/users"
              element={
                <PageTitle
                  title={`${I18n.t("routes.clientsBlockList.pageTitle")} - ${I18n.t("application.name")}`}
                >
                  <ClientsBlockList />
                </PageTitle>
              }
            />
            <Route
              path="block-list/users/:id"
              element={
                <PageTitle
                  title={`${I18n.t("routes.clientBlockListForm.pageTitle")} - ${I18n.t("application.name")}`}
                >
                  <ClientBlockListForm />
                </PageTitle>
              }
            />
            <Route
              path="block-list/cities"
              element={
                <PageTitle
                  title={`${I18n.t("routes.citiesBlockList.pageTitle")} - ${I18n.t("application.name")}`}
                >
                  <CitiesBlockList />
                </PageTitle>
              }
            />
            <Route
              path="block-list/cities/:id"
              element={
                <PageTitle
                  title={`${I18n.t("routes.cityBlockListForm.pageTitle")} - ${I18n.t("application.name")}`}
                >
                  <CityBlockListForm />
                </PageTitle>
              }
            />
            <Route
              path="client/:id"
              element={
                <PageTitle
                  title={`${I18n.t("routes.clientDetail.pageTitle")} - ${I18n.t("application.name")}`}
                >
                  <ClientDetail />
                </PageTitle>
              }
            />
            <Route
              path="client/:id/complaint/new"
              element={
                <PageTitle
                  title={`${I18n.t("routes.clientNewComplaint.pageTitle")} - ${I18n.t("application.name")}`}
                >
                  <Complaint />
                </PageTitle>
              }
            />
            <Route
              path="client/:id/complaint/:complaintId/edit"
              element={
                <PageTitle
                  title={`${I18n.t("routes.clientEditComplaint.pageTitle")} - ${I18n.t("application.name")}`}
                >
                  <Complaint />
                </PageTitle>
              }
            />
            <Route
              path="hash-agency"
              element={
                <PageTitle
                  title={`${I18n.t("routes.hashAgencyClient.pageTitle")} - ${I18n.t("application.name")}`}
                >
                  <HashAgencyClient />
                </PageTitle>
              }
            />
            <Route
              path="consolidated/clients"
              element={
                <PageTitle
                  title={`${I18n.t("routes.clientsConsolidated.pageTitle")} - ${I18n.t("application.name")}`}
                >
                  <ClientsConsolidated />
                </PageTitle>
              }
            />
            <Route
              path="consolidated/client-logins"
              element={
                <PageTitle
                  title={`${I18n.t("routes.clientLogins.pageTitle")} - ${I18n.t("application.name")}`}
                >
                  <ClientLogins />
                </PageTitle>
              }
            />
            <Route
              path="consolidated/access"
              element={
                <PageTitle
                  title={`${I18n.t("routes.accessConsolidated.pageTitle")} - ${I18n.t("application.name")}`}
                >
                  <AccessConsolidated />
                </PageTitle>
              }
            />
            <Route
              path="consolidated/access-virtual-agency"
              element={
                <PageTitle
                  title={`${I18n.t("routes.accessVirtualAgency.pageTitle")} - ${I18n.t("application.name")}`}
                >
                  <AccessHashAgency />
                </PageTitle>
              }
            />
            <Route
              path="consolidated/access-rede-facil-code"
              element={
                <PageTitle
                  title={`${I18n.t("routes.accessRedeFacilCode.pageTitle")} - ${I18n.t("application.name")}`}
                >
                  <AccessRedeFacilCode />
                </PageTitle>
              }
            />
            <Route
              path="faq"
              element={
                <PageTitle
                  title={`${I18n.t("routes.faq.pageTitle")} - ${I18n.t("application.name")}`}
                >
                  <Faq />
                </PageTitle>
              }
            />
            <Route
              path="logs"
              element={
                <PageTitle
                  title={`${I18n.t("routes.logs.pageTitle")} - ${I18n.t("application.name")}`}
                >
                  <Log />
                </PageTitle>
              }
            />
            <Route
              path="report-downloads"
              element={
                <PageTitle
                  title={`${I18n.t("routes.reportDownloads.pageTitle")} - ${I18n.t("application.name")}`}
                >
                  <ReportDownloads />
                </PageTitle>
              }
            />
            <Route
              path="*"
              element={
                <PublicRoute
                  container={<Error />}
                  title={`${I18n.t("routes.error.pageTitle")} - ${I18n.t("application.name")}`}
                />
              }
            />
          </Route>
        </Routes>
      </BrowserRouter>
    );
  }
}

export default Content;
